import { FC, useContext } from 'react';
import React from 'react';
import { useWindowWidth } from '@investoro/core';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import ChatbotContext from 'utils/context/ChatbotContext';
import { Platform, usePlatform } from 'utils/hooks/usePlatform';

import Icon, { IconEnum } from 'components/common/Icon';
import ResourcePopup from 'components/common/ResourcePopUp';

import ChatbotConversation from './ChatbotConversation';
import ChatbotWindow from './ChatbotWindow';

const ChatbotIconContainer = styled.div`
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 130px;
    height: 50px;
    border-radius: 30px;
    background-color: var(--color-alt);
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-left: 14px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.7);
    z-index: 101;

    span {
        color: #FFFFFF;
    }
    
    img {
        filter: brightness(0) invert(1);
    }
    
    @media (max-width: 1099px) {
        bottom: 80px;
        right: 15px;
        gap: 6px;
    }
`;

const ChatbotMobileIconContainer = styled.div`
    position: fixed;
    bottom: 80px;
    right: 15px;
    width: 50px;
    height: 50px;
    border-radius: 30px;
    background-color: var(--color-alt);
    display: flex;
    align-items: center;
    padding-left: 14px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.7);
    z-index: 101;

    img {
        filter: brightness(0) invert(1);
    }
`;

const NotificationBadge = styled.div`
    position: absolute;
    top: -2px;
    right: 0;
    width: 18px;
    height: 18px;
    background-color:var(--color-error);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #FFFFFF;
    font-size: 12px;
`;

const StatusIndicator = styled.div`
    position: absolute;
    bottom: -3px;
    left: 5px;
    width: 15px;
    height: 15px;
    background-color:var(--color-success);
    border-radius: 50%;
    border: 2px solid var(--color-white-bg, #f4f4f4);
`;

const IconWrapper = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--color-alt-hover);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderIconBox = styled.div`
    width: 38px;
    height: 38px;
    min-width: 36px;
    background-color: var(--color-alt);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        filter: brightness(0) invert(1);
    }
`;

const OnlineDot = styled.span`
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: var(--color-success);
`;

const OnlineStatus = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--color-success);
    font-weight: 400;
`;

const HeaderTitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 10px;
    line-height: 1.3;

    p {
        margin-left: 0;
    }
`;

const AiBadge = styled.span`
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: var(--color-alt);
    text-transform: uppercase;
    margin-bottom: 1px;
`;

const ChatbotContainer = styled.div`
    position: fixed;
    bottom: 80px;
    right: 20px;
    z-index: 200;
    
    @media (max-width: 1099px) {
        bottom: 140px;
        right: 15px;
        width: 90vw;
    }
`;

const Top = styled.div`
    border-radius: var(--box-border-radius);
    display: flex;
    flex-direction: column;
    box-shadow: 0px 7px 6px 0px rgba(0, 0, 0, 0.06);
    z-index: 2;
`;

const Header = styled.div<{ isDarkMode: boolean }>`
    padding: 10px 15px 5px 15px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${(props) => props.isDarkMode && `
        img {
            filter: brightness(0) invert(1);
        }
    `}

`;

const HeaderContent = styled.div<{ isDarkMode: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    line-height: 20px;
    color: var(--color-primary);
 
    ${(props) => props.isDarkMode && `
        img {
            filter: brightness(0) invert(1);
        }
    `}
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const NewChatButton = styled.div<{ isDarkMode: boolean }>`
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    border-radius: 8px;
    color: var(--color-primary);
    padding: 5px 10px;
    transition: background-color 0.2s ease, padding 0.15s ease;

    &:hover {
        background-color: var(--color-last-message);
    }

    ${(props) => props.isDarkMode && `
        img {
            filter: brightness(0) invert(1);
        }
    `}
`;

const ChatbotMessages: FC = () => {
    const { user } = useUser();
    const width = useWindowWidth();

    const {
        startNewPopupChat,
        isPopupOpen,
        setPopupOpen,
        popupMessages,
        isLoadingMessage,
        message,
        setMessage,
        sendMessageInPopup,
        isOpenResourcePopUp,
        closeResourcePopUp,
        suggestedQuestions,
        getMessageAudio,
        popupResetKey
    } = useContext(ChatbotContext);

    const platform = usePlatform();

    const handleSendMessage = () => {
        sendMessageInPopup(message);
    };

    const toggleChat = () => {
        setPopupOpen(!isPopupOpen);
    };

    return (
        <>
            {width < 768
                ? !isPopupOpen
                    ? <ChatbotMobileIconContainer className={'floating-chat-icon'} onClick={toggleChat}>
                        <Icon icon={IconEnum.MESSAGE} />
                    </ChatbotMobileIconContainer>
                    : null
                : <ChatbotIconContainer className={'floating-chat-icon'} onClick={toggleChat}>
                    <StatusIndicator />
                    <span>Flipchat</span>
                    <IconWrapper>
                        <Icon icon={IconEnum.MESSAGE} />
                    </IconWrapper>
                    <NotificationBadge>1</NotificationBadge>
                </ChatbotIconContainer>}
            {isPopupOpen &&
                <ChatbotContainer>
                    <ChatbotWindow isChatbot={true}>
                        <Top>
                            <Header isDarkMode={user.isDarkMode}>
                                <HeaderContent isDarkMode={user.isDarkMode} >
                                    <HeaderIconBox>
                                        <Icon icon={IconEnum.AI_STARS} />
                                    </HeaderIconBox>
                                    <HeaderTitleGroup>
                                        <AiBadge>Investoro AI</AiBadge>
                                        <p>Flipchat</p>
                                        <OnlineStatus>
                                            <OnlineDot />
                                            Online
                                        </OnlineStatus>
                                    </HeaderTitleGroup>
                                </HeaderContent>
                                <HeaderActions>
                                    <NewChatButton isDarkMode={user.isDarkMode} onClick={startNewPopupChat}>
                                        <Icon icon={IconEnum.PLUS} />
                                        <span>Nowy chat</span>
                                    </NewChatButton>
                                    <Icon icon={IconEnum.CROSS} onClick={toggleChat} />
                                </HeaderActions>
                            </Header>
                        </Top>

                        <ChatbotConversation
                            key={popupResetKey}
                            messages={popupMessages}
                            isLoadingMessage={isLoadingMessage}
                            message={message}
                            setMessage={setMessage}
                            onSendMessage={handleSendMessage}
                            onSelectSuggestion={sendMessageInPopup}
                            isDarkMode={user.isDarkMode}
                            setPopupOpen={setPopupOpen}
                            suggestedQuestions={suggestedQuestions}
                            onGetMessageAudio={getMessageAudio}
                        />
                    </ChatbotWindow>
                </ChatbotContainer>
            }
            {platform !== Platform.IOS && <ResourcePopup onClose={closeResourcePopUp} isOpen={isOpenResourcePopUp} availableResource={{ smsCount: null, tokenCount: 0 }} />}

        </>
    );
};

export default ChatbotMessages;
